import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import BrandShell from "../components/BrandShell";
import SEO from "../components/SEO";
import { useAuth } from "../lib/auth";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  User, Mail, Phone, MapPin, Lock, ShieldCheck, Upload, CheckCircle2,
  Clock, XCircle, IdCard, Save, KeyRound, Link2,
} from "lucide-react";
import { SocialConnections } from "../components/SocialConnections";
import { EmailPreferences } from "../components/EmailPreferences";
import { uploadFile } from "../lib/upload";

// Wrapper: brands live inside BrandShell (with sidebar); creators use the top Navbar.
function Layout({ role, children }) {
  if (role === "brand") return <BrandShell title="Account Settings">{children}</BrandShell>;
  return (
    <div className="min-h-screen grain">
      <Navbar />
      {children}
    </div>
  );
}

function Section({ icon: Icon, title, desc, children, testId }) {
  return (
    <div className="glass rounded-2xl p-6 md:p-7 space-y-5" data-testid={testId}>
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-white/[0.04] hairline flex items-center justify-center shrink-0">
          <Icon className="h-5 w-5 text-nexus-pink" />
        </div>
        <div className="min-w-0">
          <h2 className="font-display font-bold text-xl tracking-tight">{title}</h2>
          {desc && <p className="text-sm text-zinc-400 mt-0.5">{desc}</p>}
        </div>
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}

const inputCls = "bg-white/[0.02] border-white/10 text-white placeholder:text-zinc-600";

const DOC_TYPES = [
  { v: "passport", l: "Passport" },
  { v: "driving_licence", l: "Driving Licence" },
  { v: "national_id", l: "National ID" },
  { v: "companies_house", l: "Companies House certificate (brands)" },
  { v: "business_registration", l: "Business registration / VAT (brands)" },
];

const COUNTRIES = ["United Kingdom", "Ireland", "United States", "Canada", "Australia", "New Zealand", "France", "Germany", "Spain", "Italy", "Netherlands", "Other"];

function IdentityStatus({ doc }) {
  if (!doc) return null;
  const map = {
    pending: { c: "amber", i: Clock, l: "Under review — usually within 24 hours" },
    approved: { c: "emerald", i: CheckCircle2, l: "Approved" },
    rejected: { c: "red", i: XCircle, l: `Rejected${doc.reject_reason ? ` — ${doc.reject_reason}` : ""}` },
  };
  const m = map[doc.status] || map.pending;
  const Icon = m.i;
  return (
    <div
      data-testid={`id-doc-status-${doc.status}`}
      className={`p-3 rounded-xl bg-${m.c}-500/10 border border-${m.c}-500/30 text-${m.c}-100 text-sm flex items-center gap-2`}
    >
      <Icon className="h-4 w-4 shrink-0" />
      <div className="min-w-0">
        <div className="font-semibold">{m.l}</div>
        <div className="text-[11px] opacity-80">Type: {(DOC_TYPES.find((d) => d.v === doc.document_type) || {}).l || doc.document_type}</div>
      </div>
    </div>
  );
}

export default function AccountSettings() {
  const { user, setUser, loading } = useAuth();
  const nav = useNavigate();
  const fileRef = useRef(null);

  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    name: "", phone: "", address_line1: "", address_line2: "",
    city: "", postcode: "", country: "United Kingdom",
    company: "",
  });

  // Email change flow
  const [emailStep, setEmailStep] = useState("idle"); // idle | need_code
  const [newEmail, setNewEmail] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [devCode, setDevCode] = useState(null);
  const [emailBusy, setEmailBusy] = useState(false);

  // Password change
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwBusy, setPwBusy] = useState(false);

  // Identity doc
  const [docType, setDocType] = useState("passport");
  const [identityDoc, setIdentityDoc] = useState(null);
  const [docBusy, setDocBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/auth?mode=login"); return; }
    api.get("/auth/me").then((r) => {
      const u = r.data.user || {};
      setProfile({
        name: u.name || "",
        phone: u.phone || "",
        address_line1: u.address_line1 || "",
        address_line2: u.address_line2 || "",
        city: u.city || "",
        postcode: u.postcode || "",
        country: u.country || "United Kingdom",
        company: u.company || "",
      });
      setIdentityDoc(u.identity_document || null);
      if (u.pending_email) { setEmailStep("need_code"); setNewEmail(u.pending_email); }
    }).catch(() => { /* silent */ });
  }, [loading, user]);

  const saveProfile = async () => {
    setSaving(true);
    try {
      const payload = { ...profile };
      if (user.role !== "brand") delete payload.company;
      const r = await api.put("/auth/me", payload);
      toast.success("Details saved");
      if (setUser && r.data?.user) setUser(r.data.user);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const requestEmailChange = async () => {
    if (!newEmail.trim() || !emailPassword) { toast.error("Enter new email + your current password"); return; }
    setEmailBusy(true);
    try {
      const r = await api.post("/auth/change-email/request", { new_email: newEmail.trim(), current_password: emailPassword });
      setEmailStep("need_code");
      setDevCode(r.data?.dev_code || null);
      setEmailPassword("");
      toast.success(r.data?.delivered ? "Check your new inbox for the code" : "Code generated (dev mode)");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setEmailBusy(false); }
  };

  const confirmEmailChange = async () => {
    if (!emailCode.trim()) { toast.error("Enter the 6-digit code"); return; }
    setEmailBusy(true);
    try {
      const r = await api.post("/auth/change-email/confirm", { code: emailCode.trim() });
      toast.success("Email updated");
      setEmailStep("idle"); setNewEmail(""); setEmailCode(""); setDevCode(null);
      if (setUser && r.data?.user) setUser(r.data.user);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setEmailBusy(false); }
  };

  const changePassword = async () => {
    if (!pwCurrent || !pwNew) { toast.error("Fill both password fields"); return; }
    if (pwNew.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pwCurrent, new_password: pwNew });
      toast.success("Password updated");
      setPwCurrent(""); setPwNew("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setPwBusy(false); }
  };

  const uploadIdentity = async (file) => {
    if (!file) return;
    setDocBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = { data: await uploadFile(file, { label: "document" }) };
      const r = await api.post("/auth/identity-document", { url: up.data.url, document_type: docType });
      setIdentityDoc(r.data.identity_document);
      toast.success("ID uploaded — under review");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDocBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (!user) return null;

  return (
    <Layout role={user.role}>
      <SEO title="Account Settings · Nexus" description="Update your name, contact details, address and verify your identity." path="/settings" />
      <div className={user.role === "brand" ? "p-6 lg:p-10 space-y-6 max-w-4xl" : "max-w-4xl mx-auto px-6 lg:px-10 py-10 space-y-6"}>
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2">Account</div>
          <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tighter">
            Settings
          </h1>
          <p className="text-zinc-400 mt-1.5 text-sm">Manage your personal details, sign-in credentials and identity verification.</p>
        </div>

        {/* Personal details */}
        <Section icon={User} title="Personal details" desc="Your name and (for brands) trading name." testId="settings-personal">
          <Field label={user.role === "brand" ? "Contact name" : "Full name"}>
            <Input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className={inputCls} data-testid="settings-name-input" />
          </Field>
          {user.role === "brand" && (
            <Field label="Company / trading name">
              <Input value={profile.company} onChange={(e) => setProfile({ ...profile, company: e.target.value })} className={inputCls} data-testid="settings-company-input" />
            </Field>
          )}
        </Section>

        {/* Contact + address */}
        <Section icon={MapPin} title="Contact & address" desc="Where we can reach you and where you're based." testId="settings-address">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Phone">
              <div className="relative">
                <Phone className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} placeholder="+44 7…" className={`pl-9 ${inputCls}`} data-testid="settings-phone-input" />
              </div>
            </Field>
            <Field label="Country">
              <Select value={profile.country} onValueChange={(v) => setProfile({ ...profile, country: v })}>
                <SelectTrigger className={inputCls} data-testid="settings-country-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0C0C10] border-white/10 text-white">
                  {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Address line 1">
            <Input value={profile.address_line1} onChange={(e) => setProfile({ ...profile, address_line1: e.target.value })} placeholder="Flat 3, 12 Baker Street" className={inputCls} data-testid="settings-addr1-input" />
          </Field>
          <Field label="Address line 2 (optional)">
            <Input value={profile.address_line2} onChange={(e) => setProfile({ ...profile, address_line2: e.target.value })} className={inputCls} data-testid="settings-addr2-input" />
          </Field>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="City">
              <Input value={profile.city} onChange={(e) => setProfile({ ...profile, city: e.target.value })} placeholder="London" className={inputCls} data-testid="settings-city-input" />
            </Field>
            <Field label="Postcode">
              <Input value={profile.postcode} onChange={(e) => setProfile({ ...profile, postcode: e.target.value })} placeholder="SW1A 1AA" className={inputCls} data-testid="settings-postcode-input" />
            </Field>
          </div>
          <Button onClick={saveProfile} disabled={saving} className="nexus-gradient text-white border-0 rounded-full h-11 font-semibold" data-testid="settings-save-profile-btn">
            <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save details"}
          </Button>
        </Section>

        {/* Email */}
        <Section icon={Mail} title="Email address" desc="Your sign-in email. Changes require confirmation with a 6-digit code." testId="settings-email">
          <div className="text-sm text-zinc-300"><span className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Current</span>{user.email}</div>
          {emailStep === "idle" && (
            <div className="space-y-3">
              <Field label="New email">
                <Input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="you@newdomain.com" className={inputCls} data-testid="settings-new-email-input" />
              </Field>
              <Field label="Confirm with current password">
                <Input type="password" value={emailPassword} onChange={(e) => setEmailPassword(e.target.value)} className={inputCls} data-testid="settings-email-pw-input" />
              </Field>
              <Button onClick={requestEmailChange} disabled={emailBusy} className="rounded-full h-11 bg-white/10 text-white hover:bg-white/15 border border-white/10" data-testid="settings-email-request-btn">
                Send verification code
              </Button>
            </div>
          )}
          {emailStep === "need_code" && (
            <div className="space-y-3">
              <div className="p-3 rounded-xl bg-nexus-pink/10 border border-nexus-pink/30 text-sm text-nexus-pink">
                Code sent to <span className="font-semibold text-white">{newEmail}</span>. It expires in 30 minutes.
                {devCode && <div className="mt-2 text-white/90"><span className="text-[10px] uppercase tracking-widest text-nexus-pink/80">Dev code</span> · <span className="font-mono text-lg tracking-widest" data-testid="settings-email-dev-code">{devCode}</span></div>}
              </div>
              <Field label="6-digit code">
                <Input value={emailCode} onChange={(e) => setEmailCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))} placeholder="123456" className={`font-mono tracking-widest text-center text-lg ${inputCls}`} data-testid="settings-email-code-input" />
              </Field>
              <div className="flex gap-2 flex-wrap">
                <Button onClick={confirmEmailChange} disabled={emailBusy} className="nexus-gradient text-white border-0 rounded-full h-11 font-semibold" data-testid="settings-email-confirm-btn">
                  Confirm change
                </Button>
                <Button onClick={() => { setEmailStep("idle"); setEmailCode(""); setDevCode(null); }} variant="outline" className="rounded-full border-white/10 text-white hover:bg-white/5 h-11">
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </Section>

        {/* Password */}
        <Section icon={Lock} title="Password" desc="Change the password you sign in with." testId="settings-password">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Current password">
              <Input type="password" value={pwCurrent} onChange={(e) => setPwCurrent(e.target.value)} className={inputCls} data-testid="settings-pw-current-input" />
            </Field>
            <Field label="New password">
              <Input type="password" value={pwNew} onChange={(e) => setPwNew(e.target.value)} className={inputCls} data-testid="settings-pw-new-input" />
            </Field>
          </div>
          <Button onClick={changePassword} disabled={pwBusy} className="rounded-full h-11 bg-white/10 text-white hover:bg-white/15 border border-white/10" data-testid="settings-pw-save-btn">
            <KeyRound className="h-4 w-4 mr-2" /> Update password
          </Button>
        </Section>

        {/* Identity verification */}
        {user.role === "creator" && (
          <Section icon={Link2} title="Connections" desc="Link Instagram, TikTok or YouTube to verify your reach. Disconnect at any time to delete the token and all cached data." testId="settings-connections">
            <SocialConnections />
          </Section>
        )}

        <Section icon={Mail} title="Email notifications" desc="Choose which updates we email you. Push notifications in the app are unaffected." testId="settings-emails">
          <EmailPreferences />
        </Section>

        <Section icon={ShieldCheck} title={user.role === "brand" ? "Business verification" : "Identity verification"} desc={user.role === "brand" ? "Upload a director's ID or your Companies House certificate. Your briefs become visible to creators once approved (usually within 24h)." : "Upload a government-issued ID. We review manually and mark your profile as verified."} testId="settings-identity">
          <div id="identity" />
          {identityDoc && <IdentityStatus doc={identityDoc} />}
          <Field label="Document type">
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger className={inputCls} data-testid="settings-doc-type-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0C0C10] border-white/10 text-white">
                {DOC_TYPES.map((d) => <SelectItem key={d.v} value={d.v}>{d.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*,application/pdf"
              onChange={(e) => uploadIdentity(e.target.files?.[0])}
              className="hidden"
              data-testid="settings-doc-file-input"
            />
            <Button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={docBusy}
              className="rounded-full h-11 bg-white/10 text-white hover:bg-white/15 border border-white/10"
              data-testid="settings-doc-upload-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              {docBusy ? "Uploading…" : identityDoc ? "Replace document" : "Upload document"}
            </Button>
            <p className="text-[11px] text-zinc-500 mt-2 flex items-center gap-1.5">
              <IdCard className="h-3 w-3" /> Accepted: passport photo page, driving licence, national ID{user.role === "brand" ? ", Companies House certificate or VAT registration" : ""}. Max 20 MB. JPG, PNG or PDF.
            </p>
          </div>
        </Section>
      </div>
    </Layout>
  );
}
